import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  LinkOutlined,
  MoreVertOutlined,
  PauseCircleOutlined,
  SubdirectoryArrowRightOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { SHOULD_SHOW_UNIMPLEMENTED } from "../../constants"

import type { InventoryByPalletItem } from "@/graphql/codegen/graphql"
import { isNil } from "@/utils/utils"

interface ProductInventoryPalletRowActionsProps {
  row: CellContext<InventoryByPalletItem, unknown>["row"]
  canMaintain: boolean
  onHoldPut?: (item: InventoryByPalletItem) => void
  onHoldRelease?: (item: InventoryByPalletItem) => void
  onTransfer?: (item: InventoryByPalletItem) => void
  onCombinePallets?: (item: InventoryByPalletItem) => void
  onCoaView?: (item: InventoryByPalletItem) => void
}

export const ProductInventoryPalletRowActions: FC<ProductInventoryPalletRowActionsProps> = ({
  row,
  canMaintain,
  onHoldPut,
  onHoldRelease,
  onTransfer,
  onCombinePallets,
  onCoaView,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const isOnHold = row.original.isOnHold ?? false
  const canChangeHold = canMaintain && !isNil(row.original.referenceNbr)
  const shouldShowUnimplementedActions = canMaintain && SHOULD_SHOW_UNIMPLEMENTED

  const handleClick = (e: React.MouseEvent) => e.stopPropagation()

  const handleMenuOpen = () => setIsMenuOpen(true)

  const handleMenuClose = () => setIsMenuOpen(false)

  const handleHoldPut = () => {
    handleMenuClose()
    onHoldPut?.(row.original)
  }

  const handleHoldRelease = () => {
    handleMenuClose()
    onHoldRelease?.(row.original)
  }

  const handleTransfer = () => {
    handleMenuClose()
    onTransfer?.(row.original)
  }

  const handleCombinePallets = () => {
    handleMenuClose()
    onCombinePallets?.(row.original)
  }

  const handleCoaView = () => {
    handleMenuClose()
    onCoaView?.(row.original)
  }

  return (
    <Box className='flex items-center justify-end' ref={anchorElRef} onClick={handleClick}>
      <IconButton onClick={handleMenuOpen} data-testid='product-inventory-pallet.row-actions.actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='product-inventory-pallet.row-actions.actions-menu'
      >
        {canChangeHold && isOnHold && (
          <MenuItem onClick={handleHoldRelease} data-testid='product-inventory-pallet.row-actions.release-hold-item'>
            <ListItemIcon>
              <PauseCircleOutlined />
            </ListItemIcon>
            <ListItemText primary='Release Pallet Hold' />
          </MenuItem>
        )}
        {canChangeHold && !isOnHold && (
          <MenuItem onClick={handleHoldPut} data-testid='product-inventory-pallet.row-actions.put-on-hold-item'>
            <ListItemIcon>
              <PauseCircleOutlined />
            </ListItemIcon>
            <ListItemText primary='Put Pallet on Hold' />
          </MenuItem>
        )}
        {shouldShowUnimplementedActions && (
          <MenuItem onClick={handleTransfer} data-testid='product-inventory-pallet.row-actions.transfer-inventory-item'>
            <ListItemIcon>
              <SubdirectoryArrowRightOutlined />
            </ListItemIcon>
            <ListItemText primary='Transfer Inventory' />
          </MenuItem>
        )}
        {shouldShowUnimplementedActions && (
          <MenuItem
            onClick={handleCombinePallets}
            data-testid='product-inventory-pallet.row-actions.combine-pallets-item'
          >
            <ListItemIcon>
              <LinkOutlined />
            </ListItemIcon>
            <ListItemText primary='Combine Pallets' />
          </MenuItem>
        )}
        {canMaintain && <Divider />}
        <MenuItem onClick={handleCoaView} data-testid='product-inventory-pallet.row-actions.view-coa-item'>
          <ListItemIcon>
            <VisibilityOutlined />
          </ListItemIcon>
          <ListItemText primary='View CoA' />
        </MenuItem>
      </Menu>
    </Box>
  )
}
